import { Color, FontSize, FontWeight } from '@billy/design-system'
import { useMemo } from 'react'
import { XAxisProps, YAxisProps } from 'recharts'
import { LayoutType } from 'recharts/types/util/types'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { SalesReportGraphData } from '../../../types/salesReportGraphData'
import { SalesReportStatisticsVariant } from '../../../types/salesReportStatisticsVariant'
import { VERTICAL_BAR_HEIGHT } from '../constants/verticalBarHeight'
import { VERTICAL_BAR_TICKS_WIDTH } from '../constants/verticalBarTicksWidth'
import { SalesReportYAxisTick } from '../elements/SalesReportYAxisTick'
import { BarLayout } from '../types/barLayout'
import { getFormattedTickValue } from '../utils/getFormattedTickValue'
import { getGraphDomain } from '../utils/getGraphDomain'
import { getGraphTicks } from '../utils/getGraphTicks'
import { getGraphYAxisWidth } from '../utils/getGraphYAxisWidth'

const VERTICAL_GRAPH_TICKS = 3
const HORIZONTAL_GRAPH_TICKS = 6

interface GraphLayout {
  bar: BarLayout
  graph: LayoutType
}

interface UseSalesReportGraphProps {
  graphData: SalesReportGraphData[]
  variant: SalesReportStatisticsVariant
}

export const useSalesReportGraph = ({ graphData, variant }: UseSalesReportGraphProps) => {
  const { organization, isLoading } = useUserOrganization()

  const isVertical = variant === 'overview'

  const graphDomain = useMemo(() => getGraphDomain(graphData), [graphData])

  const graphProps = useMemo(() => {
    const barSize = isVertical ? undefined : VERTICAL_BAR_HEIGHT

    const layout: GraphLayout = {
      bar: isVertical ? 'vertical' : 'horizontal',
      graph: isVertical ? 'horizontal' : 'vertical',
    }

    const xAxisProps: XAxisProps = {
      ...(isVertical
        ? {
            dataKey: 'display.title',
            orientation: 'bottom',
            type: 'category',
          }
        : {
            orientation: 'top',
            ticks: getGraphTicks(graphDomain, HORIZONTAL_GRAPH_TICKS),
            tickFormatter: (value: number) => getFormattedTickValue(value, organization?.localeId),
            type: 'number',
          }),
    }

    const yAxisProps: YAxisProps = {
      fontWeight: FontWeight.Regular,
      ...(isVertical
        ? {
            color: Color.Grey300,
            domain: graphDomain,
            fontSize: FontSize.TextBodySM,
            tick: true,
            ticks: getGraphTicks(graphDomain, VERTICAL_GRAPH_TICKS),
            tickFormatter: (value: number) => getFormattedTickValue(value, organization?.localeId),
            type: 'number',
            width: getGraphYAxisWidth(graphDomain, organization?.localeId),
          }
        : {
            color: Color.Black,
            dataKey: 'display.title',
            fontSize: FontSize.TextBodyMD,
            tick: <SalesReportYAxisTick />,
            type: 'category',
            width: VERTICAL_BAR_TICKS_WIDTH,
          }),
    }

    return { barSize, layout, xAxisProps, yAxisProps }
  }, [graphDomain, isVertical, organization])

  return { isLoading, ...graphProps }
}
